import classNames from "classnames";
import { useTranslation } from "react-i18next";
import Button from "./Button";

export type Action = {
  icon?: React.ReactNode;
  onClick: (id: number | string) => void;
  title?: string;
};

export type Column<T extends Record<string, unknown>> = {
  name: string;
  title: string;
  align?: "left" | "center" | "right";
  formatter?: (value: unknown, item: T) => string;
};

export type Props<T extends Record<string, unknown>> = {
  columns: Column<T>[];
  actions?: Action[];
  data?: T[];
  title?: string;
  idProperty: keyof T;
};

const Table = <T extends Record<string, unknown>>({
  columns,
  data = [],
  title,
  actions,
  idProperty,
}: Props<T>) => {
  const { t } = useTranslation();

  return (
    <>
      {title && <h1 className="text-2xl font-bold">{title}</h1>}
      <div className="relative overflow-x-auto">
        <table className="w-full text-sm text-left text-gray-500 dark:text-gray-400">
          <thead className="text-xs text-gray-700 uppercase bg-gray-50 dark:bg-gray-700 dark:text-gray-400">
            <tr>
              {columns.map(({ title: tit, name }) => (
                <th key={name} scope="col" className="px-6 py-3 text-center">
                  {tit}
                </th>
              ))}
              {actions && actions?.length > 0 && <th> </th>}
            </tr>
          </thead>
          {data.length > 0 ? (
            <tbody>
              {data.map((item, key) => (
                <tr
                  // eslint-disable-next-line react/no-array-index-key
                  key={key}
                  className="bg-white border-b dark:bg-gray-800 dark:border-gray-700"
                >
                  {columns.map(({ name, formatter, align }) => (
                    <td
                      key={name}
                      className={classNames("px-6 py-4", {
                        "text-right": align === "right",
                        "text-center": align === "center",
                      })}
                    >
                      {formatter
                        ? formatter(item[name], item)
                        : String(item[name])}
                    </td>
                  ))}
                  {actions && actions?.length > 0 && (
                    <td className="px-6 py-4">
                      <div className="flex flex-row gap-2 justify-center">
                        {actions.map(({ icon, onClick, title: tit }, k) => (
                          <Button
                            // eslint-disable-next-line react/no-array-index-key
                            key={k}
                            icon={icon}
                            onClick={() =>
                              onClick(item[idProperty] as string | number)
                            }
                            size="small"
                          >
                            {tit}
                          </Button>
                        ))}
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          ) : (
            <tbody>
              <tr className="h-[150px]">
                <td
                  className="px-6 py-4 text-center align-middle bg-white"
                  colSpan={columns.length + 1}
                >
                  {t("No data")}
                </td>
              </tr>
            </tbody>
          )}
        </table>
      </div>
    </>
  );
};

export default Table;
